import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ShieldCheck, ArrowRight, ExternalLink } from "lucide-react";
import { PROMISES, STACK, PRIVACY_SUMMARY } from "@/components/privacy/privacyContent";

function DetailRow({ icon: Icon, title, text, href, linkLabel }) {
  return (
    <div className="flex items-start gap-3">
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground">{title}</p>
        <p className="text-sm text-muted-foreground">
          {text}
          {href && (
            <>
              {" "}
              <a href={href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center font-medium text-primary hover:underline">
                {linkLabel || href} <ExternalLink className="ml-0.5 h-3 w-3" />
              </a>
            </>
          )}
        </p>
      </div>
    </div>
  );
}

/**
 * PrivacyPromise — a compact, reusable trust blurb with a "Learn more" modal.
 * Drop it on onboarding, QR/invite, and public join surfaces.
 */
export function PrivacyPromise({ className = "" }) {
  return (
    <div
      className={`rounded-xl border border-primary/25 bg-primary/5 p-4 ${className}`}
      data-testid="privacy-promise"
    >
      <div className="flex items-start gap-3">
        <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground">Your privacy, your call</p>
          <p className="mt-0.5 text-sm leading-6 text-muted-foreground">{PRIVACY_SUMMARY}</p>

          <Dialog>
            <DialogTrigger asChild>
              <Button variant="link" size="sm" className="mt-1 h-auto px-0 text-primary" data-testid="privacy-promise-learn-more">
                Learn more <ArrowRight className="ml-1 h-3.5 w-3.5" />
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg" data-testid="privacy-promise-dialog">
              <DialogHeader>
                <DialogTitle className="font-serif text-xl">Privacy &amp; how this site is built</DialogTitle>
                <DialogDescription>
                  A plain-language look at what we collect, what we’d never do, and the tools that run Friday Conversations.
                </DialogDescription>
              </DialogHeader>

              <ScrollArea className="max-h-[55vh] pr-3">
                <div className="space-y-5">
                  <div className="space-y-4">
                    <p className="text-xs font-semibold uppercase tracking-wider text-primary">Our promises</p>
                    {PROMISES.map((p) => (
                      <DetailRow key={p.title} {...p} />
                    ))}
                  </div>

                  <div className="h-px bg-border" />

                  <div className="space-y-4">
                    <p className="text-xs font-semibold uppercase tracking-wider text-primary">Where your data lives &amp; who built it</p>
                    {STACK.map((s) => (
                      <DetailRow key={s.title} {...s} />
                    ))}
                  </div>
                </div>
              </ScrollArea>

              <DialogFooter className="sm:justify-between">
                <Button asChild variant="ghost" data-testid="privacy-promise-read-full">
                  <Link to="/privacy">Read full details</Link>
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPromise;
